import { PropsWithChildren, useMemo } from 'react'
import { isAxiosError } from 'axios'
import { createContext } from 'lib/createContext'
import {
  GetWorkerEvaluationsParams,
  WorkerEvaluationDateGroup,
} from 'api/review/getWorkReview'
import { useWorkReviewQuery } from 'queries/review'
import { useReviewEvaluateV2FilterQueryState } from '../hooks/useReviewEvaluateV2FilterQueryState'

type ReviewEvaluateV2ContextState = {
  state: {
    filters: GetWorkerEvaluationsParams
    groups: WorkerEvaluationDateGroup[]
    queryState: {
      isLoading: boolean
      isError: boolean
      errorMessage: string | undefined
      hasNextPage: boolean
      isFetchingNextPage: boolean
    }
  }
  actions: {
    setFilters: ReturnType<typeof useReviewEvaluateV2FilterQueryState>[1]
    fetchNextPage: () => void
  }
}

const [Context, useReviewEvaluateV2ContextValue] =
  createContext<ReviewEvaluateV2ContextState>({
    name: 'ReviewEvaluateV2Context',
  })

function getErrorMessage(error: unknown): string | undefined {
  if (!error) {
    return undefined
  }

  if (isAxiosError(error)) {
    const responseData = error.response?.data
    if (
      typeof responseData === 'object' &&
      responseData !== null &&
      'message' in responseData &&
      typeof responseData.message === 'string'
    ) {
      return responseData.message
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Sorry, something went wrong.'
}

export const ReviewEvaluateV2Provider = ({ children }: PropsWithChildren) => {
  const [urlFilters, setFilters] = useReviewEvaluateV2FilterQueryState()

  const apiFilters = useMemo(
    (): GetWorkerEvaluationsParams => ({
      reviewStatus: urlFilters.reviewStatus,
      startDate: urlFilters.from ?? undefined,
      endDate: urlFilters.to ?? undefined,
      locationId: urlFilters.locationId ?? undefined,
      positionId: urlFilters.positionId ?? undefined,
      workerId: urlFilters.workerId ?? undefined,
    }),
    [urlFilters]
  )

  const {
    data = { pages: [] },
    isPending,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useWorkReviewQuery(apiFilters)

  const groups = useMemo(
    () => data.pages.flatMap((page) => page.groups ?? []),
    [data]
  )

  const value = useMemo(
    (): ReviewEvaluateV2ContextState => ({
      state: {
        filters: apiFilters,
        groups,
        queryState: {
          isLoading: isPending,
          isError,
          errorMessage: getErrorMessage(error),
          hasNextPage: Boolean(hasNextPage),
          isFetchingNextPage,
        },
      },
      actions: {
        setFilters,
        fetchNextPage,
      },
    }),
    [
      apiFilters,
      groups,
      isPending,
      isError,
      error,
      hasNextPage,
      isFetchingNextPage,
      setFilters,
      fetchNextPage,
    ]
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useReviewEvaluateV2ContextValue }
